<template>
  <DetailPage>
    <template #left-nav>
      <button class="link link-main icon-link" @click="modalInstance.dismiss()">
        <Icon icon="left" /> {{ $t('GENERAL.BACK') }}
      </button>
    </template>
    <template #central-nav>
      <h2>{{ $t('DESIGNS.MULTI_CREATION.PANEL.HEADING') }}</h2>
    </template>
    <template #right-nav>
      <VDropdown :disabled="selection.isComplete.value" :triggers="['hover']">
        <ProgressButton
          class="btn btn-primary"
          :on-click="apply"
          label="GENERAL.APPLY"
          :disabled="!selection.isComplete.value"
        />
        <template #popper>
          {{ $t('DESIGNS.MULTI_CREATION.MINIMUM') }}
        </template>
      </VDropdown>
    </template>
    <template #content>
      <div class="multi-print-area-create">
        <MultiPrintAreaPlacement :selection="selection" />
        <!-- One grid item, not two: a third child would move the list into the slot column. -->
        <div class="design-column">
          <p v-if="failed" class="general-error">
            {{ $t('DESIGNS.MULTI_CREATION.GENERAL_ERROR') }}
          </p>
          <MultiPrintAreaDesignPicker :selection="selection" />
        </div>
      </div>
    </template>
  </DetailPage>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import MultiPrintAreaDesignPicker from './MultiPrintAreaDesignPicker.vue';
import MultiPrintAreaPlacement from './MultiPrintAreaPlacement.vue';
import publishingCoreService from 'commons/api/publishingCoreService/publishingCoreService';
import { usePublishingStore } from 'commons/stores/publishing';
import { useMultiPrintAreaSelection } from 'commons/composition/multiPrintAreaSelection';
import analytics from 'commons/tracking/analytics';

/**
 * Assembles one multi-print-area composition. Nothing is written until "Apply" — see
 * `docs/publishing/multi-print-area-creation.md`.
 */
const props = defineProps<{
  modalInstance: { close: () => void; dismiss: () => void };
}>();

const publishingStore = usePublishingStore();
const selection = useMultiPrintAreaSelection();

const failed = ref(false);

const apply = async () => {
  failed.value = false;
  const designIds = [...selection.designIds.value];

  try {
    await publishingCoreService.createCompositionFromDesignIdsWithScope({
      designIds,
      mainDesignId: selection.mainDesignId.value as number,
      hotspotConfigs: selection.hotspotConfigs.value,
      scope: publishingStore.currentScope,
    });
  } catch (error) {
    failed.value = true;
    return Promise.reject(error);
  }

  analytics.logEvent('composition_multi_print_area_created', {
    value: designIds.length,
    designs: designIds.length,
  });

  props.modalInstance.close();
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';

.multi-print-area-create {
  width: 100%;
  padding: 24px 24px 48px 24px;
  display: grid;
  // `minmax(0, 1fr)`, not `1fr`: a track floors at its content width, so a long design name would
  // widen the column. Both `align-*` lines are what gives the sticky panel room to travel — see
  // `docs/publishing/multi-print-area-creation.md` §3.
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  align-self: flex-start;

  @media (max-width: 1099px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

// Lets a long design name truncate instead of widening the column.
.design-column {
  min-width: 0;
}

.general-error {
  color: $pa-color-red;
  font-weight: bold;
  margin: 0 0 16px 0;
}
</style>
